import type { CodeContent } from '@/types/github';
import type { QueryClient } from '@tanstack/react-query';
import pLimit from 'p-limit';

import { ensurePrV2FileContent } from './use-pr-v2-file-content';

export type HydrateOmittedCodeContentsOptions = {
  /**
   * Comparison SHAs of the code-changes payload that produced `files`. Required (nullable) so
   * every caller has to thread the comparison through: hydrating without them would let the
   * per-file endpoint fall back to the canonical summary SHAs — the wrong file versions under
   * custom/last-reviewed comparisons — and would cache entries under a query key that diverges
   * from the hook consumers'. When either SHA is unknown the files are returned untouched.
   */
  headSha: string | null;
  baseSha: string | null;
  /** Restrict hydration to a subset of the stripped files (default: hydrate every stripped file). */
  shouldHydrateFile?: (file: CodeContent) => boolean;
};

// Cap the per-file fan-out so a large PR does not fire hundreds of content requests at once.
const HYDRATION_CONCURRENCY_LIMIT = 8;

/**
 * Rehydrates old/new file contents for entries stripped from the code-changes payload
 * (`contentsOmitted === true`) using the per-file lazy-content endpoint.
 *
 * Returns a new array with hydrated copies; untouched entries keep their identity (and the input
 * array is returned as-is when nothing needs hydration). A file whose fetch fails — or resolves
 * without contents — stays stripped, so consumers degrade per-file rather than wholesale.
 */
export async function hydrateOmittedCodeContents(
  queryClient: QueryClient,
  owner: string,
  repo: string,
  number: string,
  files: CodeContent[],
  options: HydrateOmittedCodeContentsOptions,
): Promise<CodeContent[]> {
  const { headSha, baseSha } = options;
  const shouldHydrateFile = options.shouldHydrateFile ?? (() => true);
  // Without a blob sha the per-file endpoint cannot resolve contents (matching the disabled
  // state of usePrV2FileContent), so those entries stay stripped.
  const needsHydration = (file: CodeContent): boolean =>
    file.contentsOmitted === true && Boolean(file.sha) && shouldHydrateFile(file);

  // Without both comparison SHAs the endpoint would fall back to the canonical summary SHAs (a
  // potentially wrong file version), so degrade the same way as a per-file failure: every entry
  // stays stripped.
  if (!headSha || !baseSha || !files.some(needsHydration)) {
    return files;
  }

  const limit = pLimit(HYDRATION_CONCURRENCY_LIMIT);

  return Promise.all(
    files.map(async (file) => {
      const { sha } = file;
      if (!needsHydration(file) || !sha) {
        return file;
      }

      try {
        const contents = await limit(() =>
          ensurePrV2FileContent(queryClient, owner, repo, number, {
            filename: file.filename,
            previousFilename: file.previous_filename ?? null,
            sha,
            status: file.status,
            headSha,
            baseSha,
          }),
        );

        // Stripped entries always had at least one textual side; a both-null response means the
        // endpoint could not recover the contents, so keep the entry marked as omitted.
        if (contents.error || (contents.oldContent === null && contents.newContent === null)) {
          return file;
        }

        return {
          ...file,
          oldContent: contents.oldContent,
          newContent: contents.newContent,
          contentsOmitted: false,
        };
      } catch {
        return file;
      }
    }),
  );
}
